import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Colors, ColorValues, Media, Tab } from 'Types';
import { useAxios, useGetB2bUserProfileQuery, useModal } from 'Hooks';
import {
	Card,
	LoadingContent,
	ModalBoxChangeImage,
	ShimmerImage,
	TabNavigation,
	FontAwesome,
	Button
} from 'Elements';
import pageStyles from 'partials/shared/page.module.scss';
import styles from 'partials/decoration/user-decoration-box.module.scss';
import { useState } from 'react';
import { api } from 'src/helpers/API';
import { toastify } from 'src/helpers/Toastify';
import { names } from 'src/constants/Names';
import { useTranslation } from 'react-i18next';
import { ConfirmRemoveUser } from 'src/components/elements/modal/ConfirmRemoveUser';

export const UserDecoration = (props) => {
	const [deleteUserModal, setDeleteUserModal] = useState(false);
	const { sendRequest, isLoading } = useAxios();
	const { t } = useTranslation();
	const [openModal, toggleModal] = useModal();
	const { pathname } = useLocation();
	const { user_id } = useParams<'user_id'>();
	const { data: userProfile } = useGetB2bUserProfileQuery();
	const { users } = names;
	const navigate = useNavigate();

	const name =
		props?.user?.firstName || props?.user?.lastName
			? `${props?.user?.firstName} ${props?.user?.lastName}`
			: '...';
	const defaultImage = '/assets/img/profiles/img-user-default.png';

	const defaultTab =
		pathname === `/users/${user_id}` ||
		pathname === `/users/${user_id}/general`;

	const tabs: Tab[] = [
		{
			id: 0,
			title: 'tab.general',
			path: 'general',
			isActive: defaultTab
		},
		{
			id: 1,
			title: 'tab.address',
			path: 'address',
			isActive: pathname === `/users/${user_id}/address`
		}
	];

	const handleResetImage = async () => {
		props?.setImage({
			url: '',
			thumbnail: ''
		});
		props?.setValue(
			'avatar',
			{
				url: '',
				thumbnail: ''
			},
			{ shouldDirty: true }
		);
	};

	const handleUploadImage = async (image_data: Media) => {
		if (image_data) {
			props?.setImage({
				url: image_data.absoluteUri,
				thumbnail: image_data.thumbnailUrl
			});
			props?.setValue(
				'avatar',
				{
					url: image_data.absoluteUri,
					thumbnail: image_data.thumbnailUrl
				},
				{ shouldDirty: true }
			);
		}
	};
	const handleDeleteUser = async () => {
		if (!props?.user) return;

		const { id } = props.user;
		await sendRequest(
			api.deleteUser(id),
			{
				method: 'DELETE'
			},
			() => {
				toastify('toastify.user-deleted-successfully', {
					type: 'success'
				});
				setDeleteUserModal(false);
				navigate(users.path);
			}
		);
	};
	return (
		<Card tab>
			{props?.isFetching && <LoadingContent />}
			<div className="d-flex align-items-center mb-3 position-relative">
				<div
					role="button"
					tabIndex={-1}
					className={styles.avatarBox}
					onClick={toggleModal}>
					<div className={styles.avatar}>
						<ShimmerImage
							width={120}
							height={120}
							src={props?.image?.thumbnail ?? defaultImage}
							defaultImage={defaultImage}
							className={styles.shimmer}
							alt={name}
						/>
					</div>
					<span className={styles.edit}>
						<FontAwesome
							icon="pen"
							size="xl"
							color={ColorValues['gray-1']}
						/>
					</span>
				</div>
				<div className="flex-grow-1">
					<h6 className="fs-6 fw-400 lh-base mb-1">{name}</h6>
					<span className="text-gray-3 lh-base mb-2 d-flex">
						{props?.user?.emailAddress ?? '...'}
					</span>
				</div>
				{props?.user?.emailAddress !== userProfile?.emailAddress && (
					<div>
						<Button
							color={Colors['white-red']}
							onClick={() => setDeleteUserModal(true)}>
							<FontAwesome icon="trash" className="me-2" />
							<span>{t('button.delete-user')}</span>
						</Button>
					</div>
				)}
			</div>
			<div className={pageStyles.tab}>
				{tabs?.map((tab) => {
					return (
						<TabNavigation
							key={tab.id}
							to={tab.path}
							title={tab.title}
							isActive={tab.isActive}
						/>
					);
				})}
			</div>
			<ConfirmRemoveUser
				isOpen={deleteUserModal}
				onClose={() => setDeleteUserModal(false)}
				onSubmit={handleDeleteUser}
				isSubmitting={isLoading}
				user={name}
			/>
			<ModalBoxChangeImage
				name="imageUrl"
				isOpen={openModal}
				title="title.change-image"
				image={props?.image?.thumbnail || ''}
				defaultImage={defaultImage}
				onRemove={handleResetImage}
				onClose={toggleModal}
				onImageChange={handleUploadImage}
			/>
		</Card>
	);
};
